import React, { useState, useRef, useEffect, useMemo } from "react";
import { SERVER_URL } from "../config";
import { buildViewerPaymentSummary } from "../utils/paymentSettlement";
import { openGameHelp } from "../utils/openGameHelp";

const PHASE_LABELS = {
  waiting: "Waiting for players...",
  suitvote: "Vote for Dealer Suit",
  dealer_reveal: "Finding Dealer...",
  deal: "Dealing cards...",
  action: "Deciding knock/discard...",
  reveal: "Revealing knocked hands...",
  results: "Hand Complete!",
};

const suitSymbols = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
const suitColors = { spades: "#333", clubs: "#333", hearts: "#d22", diamonds: "#d22" };
const SUIT_DISPLAY = { spades: "Spades", hearts: "Hearts", diamonds: "Diamonds", clubs: "Clubs" };
const SUIT_KEYS = ["spades", "hearts", "diamonds", "clubs"];
const SEAT_FIXED_WIDTH = 150; // Reduced size for better table visibility

// Modern design system
const COLORS = {
  primary: "#4f46e5",
  primaryHover: "#4338ca",
  secondary: "#64748b",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  background: "#f8fafc",
  surface: "#ffffff",
  border: "#e2e8f0",
  text: "#1e293b",
  textLight: "#64748b",
  textMuted: "#94a3b8",
};

const SHADOWS = {
  sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
};

const ANIMATIONS = {
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  hover: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
};

export default function TableSidebar({ table, player, session, socket, onTakeBreak, onQuit }) {
  const [chatMsg, setChatMsg] = useState("");
  const [historyTab, setHistoryTab] = useState(false);
  const [dealerReadyClicked, setDealerReadyClicked] = useState(false);
  const [dealerHighlight, setDealerHighlight] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [tempMessage, setTempMessage] = useState(null);
  const [selectedAnteAmount, setSelectedAnteAmount] = useState(2);
  const chatListEnd = useRef(null);
  const historyRef = useRef(null);

  // Calculate payment tracking relative to current user
  const allSeats = useMemo(() => (table?.players || []).concat(table?.bots || []), [table]);
  const state = table?.state ?? {};
  const phase = state.phase;
  const dealerIdx = state.dealerIdx;
  const dealerSuit = state.dealerSuit;
  const hands = state.hands || [];
  const activePlayerIdx = state.activePlayerIdx;
  const knocked = state.knocked || [];
  const discarded = state.discarded || [];
  const revealOrder = state.revealOrder || [];
  const revealedCount = state.revealedCount || 0;
  const winnerIdx = state.winnerIdx;
  const readies = state.readies || [];
  const readyPlayers = state.readyPlayers || [];
  const leavingNotification = state.leavingNotification;
  const suitVotes = state.suitVotes || [];
  const historyArr = state.history || [];
  const round = state.round;
  const dealerRevealDeck = useMemo(() => state.dealerRevealDeck || [], [state.dealerRevealDeck]);
  const dealerRevealIdx = state.dealerRevealIdx;
  const dealerRevealFound = state.dealerRevealFound;
  const dealerRevealDone = state.dealerRevealDone;
  const chat = useMemo(() => (table && table.chat ? table.chat : []), [table, table?.chat]);
  const spectators = useMemo(() => (state && state.spectators ? state.spectators : []), [state, state?.spectators]);
  
             // Ante and Credit System
  const credits = state.credits || {};
  const pot = state.pot || 0;
  const anteCollected = state.anteCollected || false;
  const paymentHistory = useMemo(() => state.paymentHistory || [], [state, state?.paymentHistory]);
  const freeHands = state.freeHands || {};
  const creditValue = state.creditValue || 1.00;
  const [paymentTab, setPaymentTab] = useState(false);
         
         // Ante Request Voting System
         const anteRequest = state.anteRequest || null;
         const anteVotes = state.anteVotes || {};
         const anteVoteComplete = state.anteVoteComplete || false;
         const anteVoteResult = state.anteVoteResult || null;
         
         // Credit Increase Request System
         const creditIncreaseRequest = state.creditIncreaseRequest || null;
         const creditIncreaseVotes = state.creditIncreaseVotes || {};
         const creditIncreaseVoteComplete = state.creditIncreaseVoteComplete || false;
         const creditIncreaseVoteResult = state.creditIncreaseVoteResult || null;

  const calculatePaymentTracking = useMemo(() => {
    if (!player?.playerID || !paymentHistory?.length || !allSeats?.length) return [];
    return buildViewerPaymentSummary(allSeats, paymentHistory, player.playerID);
  }, [paymentHistory, player?.playerID, allSeats]);

  const myIdx = allSeats.findIndex((p) => p.playerID === player?.playerID);
  const isHost = allSeats.length > 0 && allSeats[0]?.playerID === player?.playerID;
  const isSpectate = !!player?.isSpectator;

  // ========== Dealer Reveal: Which latest card is at each seat? ==========
  const seatCount = allSeats.length;
  let lastCardForSeat = Array(seatCount).fill(null);
  if (dealerRevealDeck && typeof dealerRevealIdx === "number" && dealerRevealIdx >= 0) {
    for (let i = 0; i <= dealerRevealIdx; ++i) {
      const seatIdx = i % seatCount;
      lastCardForSeat[seatIdx] = dealerRevealDeck[i];
    }
  }



  useEffect(() => {
    // Only auto-scroll chat if user is already near the bottom
    if (chatListEnd.current) {
      const chatContainer = chatListEnd.current.parentElement;
      if (chatContainer) {
        const isNearBottom = chatContainer.scrollTop + chatContainer.clientHeight >= chatContainer.scrollHeight - 50;
        if (isNearBottom) {
          chatListEnd.current.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  }, [chat]);

  // Reset dealerReadyClicked when leaving dealer_selected or once server confirms ready
  useEffect(() => {
    if (phase !== "dealer_selected") {
      setDealerReadyClicked(false);
    }
  }, [phase]);

  useEffect(() => {
    if (readies.includes(player?.playerID)) {
      setDealerReadyClicked(false);
    }
  }, [readies, player?.playerID]);

  // Dealer Reveal Auto-Progress
  useEffect(() => {
    if (!table || !player) return;
    if (phase !== "dealer_reveal") return;
    const idx = dealerRevealIdx == null ? -1 : dealerRevealIdx;
    if (
      !dealerRevealDone &&
      dealerRevealDeck &&
      dealerRevealDeck.length > 0 &&
      idx < dealerRevealDeck.length - 1 &&
      dealerRevealFound == null
    ) {
      // Start immediately if idx is -1 (phase just started), otherwise wait 2 seconds
      const delay = idx === -1 ? 500 : 2000;
      const timer = setTimeout(() => {
        socket.emit("dealerRevealNext", {
          tableID: table.id,
          prevIdx: idx,
        });
      }, delay);
      return () => clearTimeout(timer);
    }
    if (dealerRevealDone && dealerRevealFound != null && !dealerHighlight) {
      const t = setTimeout(() => setDealerHighlight(true), 2200);
      return () => clearTimeout(t);
    }
  }, [
    table, player, phase, dealerRevealDone,
    dealerRevealDeck, dealerRevealIdx, dealerRevealFound, dealerHighlight, socket,
  ]);

  // Handle temporary message display for new chat messages
  useEffect(() => {
    if (chat && chat.length > 0) {
      const lastMessage = chat[chat.length - 1];
      if (!lastMessage.system && lastMessage.name !== player?.name) {
        setTempMessage(lastMessage);
        setTimeout(() => setTempMessage(null), 3000);
      }
    }
  }, [chat, player?.name]);

  // Auto-clear ante vote result after 3 seconds
  useEffect(() => {
    if (anteVoteComplete && anteVoteResult) {
      const timer = setTimeout(() => {
        // The server will clear the ante request state after a delay
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [anteVoteComplete, anteVoteResult]);

  const scrollHistoryToTop = () => {
    if (historyRef.current) historyRef.current.scrollTop = 0;
  };

  if (!table || !player) {
    return (
      <div style={{ 
        textAlign: "center", 
        marginTop: 70, 
        fontSize: 22,
        color: COLORS.text,
        fontFamily: "'Inter', system-ui, sans-serif"
      }}>
        Loading game...
      </div>
    );
  }

  // Enhanced styling functions
  function seatBackground(isWinner, isDealerFound) {
    if (isDealerFound) return "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)";
    if (isWinner) return "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)";
    return COLORS.surface;
  }

  function seatBorder(isWinner, isDealerFound) {
    if (isDealerFound) return `2px solid ${COLORS.success}`;
    if (isWinner) return `2px solid ${COLORS.warning}`;
    return `1px solid ${COLORS.border}`;
  }

  function displayAvatar(p) {
    return p.avatarUrl && p.avatarUrl.trim() !== ""
      ? p.avatarUrl
      : "/avatars/avatar1.png";
  }

  function shouldShowCards(idx) {
    if (isSpectate) return false;
    if (phase === "action") return idx === myIdx && idx === activePlayerIdx;
    if (phase === "reveal") {
      const knockIdx = revealOrder.indexOf(idx);
      return (
        knocked &&
        knocked.includes(idx) &&
        knockIdx > -1 &&
        knockIdx < revealedCount
      );
    }
    if (phase === "results" || phase === "done") return knocked && knocked.includes(idx);
    return false;
  }

  function playerIndicators(idx, isBot) {
    if (discarded && discarded.includes(idx))
      return (
        <span style={{ 
          color: COLORS.danger, 
          fontWeight: "600",
          fontSize: "0.875rem",
          padding: "2px 8px",
          background: "#fef2f2",
          borderRadius: "12px",
          border: `1px solid ${COLORS.danger}20`
        }}>
          Discarded
        </span>
      );
    if (knocked && knocked.includes(idx))
      return (
        <span style={{ 
          color: COLORS.success, 
          fontWeight: "600",
          fontSize: "0.875rem",
          padding: "2px 8px",
          background: "#f0fdf4",
          borderRadius: "12px",
          border: `1px solid ${COLORS.success}20`
        }}>
          Knocked
        </span>
      );
    return (
      <span
        style={{
          fontSize: "0.875rem",
          color: isBot ? COLORS.success : COLORS.primary,
          fontWeight: "600",
          padding: "2px 8px",
          background: isBot ? "#f0fdf4" : "#eff6ff",
          borderRadius: "12px",
          border: `1px solid ${isBot ? COLORS.success : COLORS.primary}20`
        }}
      >
        {isBot ? "Bot" : "Human"}
      </span>
    );
  }

  // Event handlers
  function handleKnock() {
    if (!socket || isSpectate) return;
    socket.emit("playerAction", {
      tableID: table.id,
      playerID: player.playerID,
      action: "knock",
    });
  }

  function handleDiscard() {
    if (!socket || isSpectate) return;
    socket.emit("playerAction", {
      tableID: table.id,
      playerID: player.playerID,
      action: "discard",
    });
  }

  function handleSuitVote(suitKey) {
    if (!socket || isSpectate) return;
    socket.emit("suitVote", {
      tableID: table.id,
      playerID: player.playerID,
      suit: suitKey,
    });
  }

  function handleReadyForSuitVote() {
    if (!socket || isSpectate) return;
    socket.emit("readyForSuitVote", {
      tableID: table.id,
      playerID: player.playerID,
    });
  }

  function handleNextRoundReady() {
    if (!socket || isSpectate) return;
    socket.emit("nextRoundReady", {
      tableID: table.id,
      playerID: player.playerID,
    });
  }

  function handleAddBot() {
    if (!socket || isSpectate) return;
    socket.emit("addBot", { tableID: table.id, playerID: player.playerID });
  }

  function sendChat() {
    const trimmed = chatMsg.trim();
    if (!socket || !trimmed || isSpectate) return;
    socket.emit("chatMessage", {
      tableID: table.id,
      playerID: player.playerID,
      msg: trimmed,
    });
    setChatMsg("");
  }

  function handleRemoveSpectator(spectatorID) {
    if (!socket || !isHost) return;
    socket.emit("removeSpectator", {
      tableID: table.id,
      requesterID: player.playerID,
      targetSpectatorID: spectatorID,
    });
  }

  function handleTakeBreak() {
    if (socket && table && player) {
      socket.emit("leaveTable", { tableID: table.id, playerID: player.playerID });
    }
    if (typeof onTakeBreak === "function") onTakeBreak();
  }

  function handleQuitPermanent() {
    if (socket && table && player) {
      socket.emit("quitTable", { tableID: table.id, playerID: player.playerID });
    }
    if (typeof onQuit === "function") onQuit();
  }

  // Ante Request and Voting Handlers
  function handleRequestAnteChange(requestedAmount = 2) {
    if (socket && table && player) {
      socket.emit("requestAnteIncrease", {
        tableID: table.id,
        playerID: player.playerID,
        requestedAmount: requestedAmount
      });
    }
  }

  function handleAnteVote(vote) {
    if (socket && table && player) {
      socket.emit("anteVote", {
        tableID: table.id,
        playerID: player.playerID,
        vote: vote // "accept" or "reject"
      });
    }
  }

  function handleCancelAnteRequest() {
    if (socket && table && player) {
      socket.emit("cancelAnteRequest", {
        tableID: table.id,
        playerID: player.playerID
      });
    }
  }

  function handleCreditIncreaseVote(vote) {
    console.log(`DEBUG - Client: handleCreditIncreaseVote called with vote=${vote}`);
    if (socket && table && player) {
      console.log(`DEBUG - Client: Emitting creditIncreaseVote with tableID=${table.id}, playerID=${player.playerID}, vote=${vote}`);
      socket.emit("creditIncreaseVote", {
        tableID: table.id,
        playerID: player.playerID,
        vote: vote // "accept" or "reject"
      });
    } else {
      console.log(`DEBUG - Client: Cannot emit vote - socket=${!!socket}, table=${!!table}, player=${!!player}`);
    }
  }

  function handleGeneratePaymentReport() {
    const reportUrl = `${window.location.origin}/payment-report/?table=${encodeURIComponent(table.id || "")}`;
    const newWindow = window.open(reportUrl, '_blank', 'width=800,height=600');
    
    if (newWindow) {
      newWindow.onload = () => {
        // The new window will load the payment report page
      };
    }
  }

  const inviteLink = window.location.origin + "/?invite=1&table=" + (table.id || "");
  const tableCode = table.id || "";
  const readyList = readyPlayers || [];
  const resultReadies = readies || [];
  const isFirstHand = (round === 1 || !historyArr || historyArr.length === 0);

  const mySeatIdx = allSeats.findIndex((s) => s.playerID === player?.playerID);
  const playerNeedsAction = !isSpectate && (
    (phase === "action" && mySeatIdx === activePlayerIdx) ||
    (phase === "dealer_reveal" && dealerRevealFound != null && dealerRevealDone && !(readies && readies.includes(player.playerID))) ||
    (phase === "waiting" && isFirstHand && !readyList.includes(player.playerID)) ||
    (phase === "results" && !resultReadies.includes(player.playerID))
  );

  return (
    <>
    <div style={{
      minHeight: "100vh",
      background: COLORS.background,
      fontFamily: "'Inter', system-ui, sans-serif",
      color: COLORS.text,
      display: "flex",
      position: "relative"
    }}>
      {/* Sidebar */}
      <div style={{
        width: "300px",
        background: COLORS.surface,
        borderRight: `1px solid ${COLORS.border}`,
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        overflowY: "hidden",
        position: "sticky",
        top: 0,
        height: "100vh",
        boxShadow: SHADOWS.sm,
        zIndex: 100
      }}>
      {/* Temporary Message Display */}
      {tempMessage && (
        <div style={{
          position: "fixed",
          top: "20px",
          right: showChat ? "340px" : "20px",
          background: COLORS.primary,
          color: "white",
          padding: "12px 20px",
          borderRadius: "8px",
          boxShadow: SHADOWS.lg,
          zIndex: 1001,
          maxWidth: "300px",
          fontSize: "0.875rem",
          fontWeight: "600"
        }}>
          <strong>{tempMessage.name || "Player"}:</strong> {tempMessage.text}
        </div>
      )}
      {/* Notification Banner */}
      {leavingNotification && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "20px",
          transform: "translateX(-50%)",
          maxWidth: 450,
          background: COLORS.surface,
          color: COLORS.text,
          fontWeight: "600",
          fontSize: "1rem",
          border: `1px solid ${COLORS.border}`,
          borderRadius: "12px",
          textAlign: "center",
          padding: "16px 24px",
          zIndex: 200,
          boxShadow: SHADOWS.lg,
          display: "flex",
          alignItems: "center",
          gap: "12px"
        }}>
          <img 
            src={leavingNotification.avatar || "/avatars/avatar1.png"}
            style={{ 
              width: 40, 
              height: 40, 
              borderRadius: "50%", 
              border: `2px solid ${COLORS.border}` 
            }} 
            alt="avatar" 
          />
          {leavingNotification.msg}
        </div>
      )}

      {/* Ante Collection Notice */}
      {phase === "deal" && !anteCollected && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 400,
          background: COLORS.warning,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.warning}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "24px 32px",
          zIndex: 1000,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            💰
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            Collecting Antes
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
            Each player contributes 1 chip to the pot
          </div>
        </div>
      )}

      {/* Dealer Change Notice */}
      {table.state?.showDealerChangeNotice && table.state?.willDealerChange && table.state?.dealerToBeIdx !== null && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 500,
          background: COLORS.primary,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.primary}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "32px 40px",
          zIndex: 1000,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            🎉
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            Special Win!
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
            {(() => {
              const allSeats = [...(table.players || []), ...(table.bots || [])];
              const winner = allSeats[table.state.dealerToBeIdx];
              const earnings = table.state.winnerEarnings || 0;
              const earningsDollar = (earnings * (table.state.creditValue || 1.00)).toFixed(2);
              return winner ? `${winner.name} wins with a special hand!` : "Player wins with a special hand!";
            })()}
          </div>
          <div style={{ 
            fontSize: "1rem", 
            opacity: 0.9, 
            marginTop: "8px",
            fontWeight: "600",
            color: "#fbbf24"
          }}>
            {(() => {
              const earnings = table.state.winnerEarnings || 0;
              const earningsDollar = (earnings * (table.state.creditValue || 1.00)).toFixed(2);
              return `Earned ${earnings} chips ($${earningsDollar}) from the pot!`;
            })()}
          </div>
          <div style={{ 
            fontSize: "1rem", 
            opacity: 0.9, 
            marginTop: "8px",
            fontWeight: "600"
          }}>
            Deck will be shuffled and dealer will change!
          </div>
        </div>
      )}

      {/* Dealer No Face Card Notice */}
      {table.state?.showDealerNoFaceCardNotice && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 500,
          background: COLORS.warning,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.warning}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "32px 40px",
          zIndex: 1000,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            ⚠️
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            No Winner This Hand
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
            Dealer doesn't have a face card.
          </div>
          <div style={{ 
            fontSize: "1rem", 
            opacity: 0.9, 
            marginTop: "8px",
            fontWeight: "600"
          }}>
            All players must ante next round.
          </div>
        </div>
      )}

      {/* All Discard Notice */}
      {table.state?.allDiscardNotice && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 500,
          background: COLORS.warning,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.warning}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "32px 40px",
          zIndex: 1000,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            🃏
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            No Winner This Hand
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
            All players discarded.
          </div>
          <div style={{ 
            fontSize: "1rem", 
            opacity: 0.9, 
            marginTop: "8px",
            fontWeight: "600"
          }}>
            All players must ante again for the next round.
          </div>
        </div>
      )}

      {/* Ante Request Voting Interface */}
      {anteRequest && (!anteVoteComplete || state.autoAcceptCountdown !== undefined) && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "60%",
          transform: "translate(-50%, -50%)",
          maxWidth: 500,
          background: COLORS.surface,
          color: COLORS.text,
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.border}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "32px 40px",
          zIndex: 1000,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            🗳️
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            Ante Increase Request
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4", marginBottom: "16px" }}>
            {(() => {
              const allSeats = [...(table.players || []), ...(table.bots || [])];
              const requester = allSeats.find(p => p.playerID === anteRequest.requesterID);
              const baseMessage = requester ? `${requester.name} requests to change ante to ${anteRequest.requestedAmount} chips ($${(anteRequest.requestedAmount * creditValue).toFixed(2)})` : "A player requests to change the ante";
              
              // Add auto-accept countdown message if applicable
              if (state.autoAcceptCountdown !== undefined && state.autoAcceptCountdown > 0) {
                return `${baseMessage}\n\nCredit change auto-accepted in ${state.autoAcceptCountdown} seconds...`;
              }
              
              return baseMessage;
            })()}
          </div>
          
          {/* Show voting buttons only if this player can vote and it's not an auto-accept scenario */}
          {anteVotes.hasOwnProperty(player?.playerID) && anteVotes[player?.playerID] === null && state.autoAcceptCountdown === undefined && (
            <div style={{ display: "flex", gap: "16px", marginTop: "16px" }}>
              <button
                onClick={() => handleAnteVote("accept")}
                style={{
                  padding: "12px 24px",
                  background: COLORS.success,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "700",
                  fontSize: "1rem",
                  cursor: "pointer",
                  transition: ANIMATIONS.hover,
                  boxShadow: SHADOWS.sm
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#059669";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = SHADOWS.md;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = COLORS.success;
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = SHADOWS.sm;
                }}
              >
                Accept
              </button>
              <button
                onClick={() => handleAnteVote("reject")}
                style={{
                  padding: "12px 24px",
                  background: COLORS.danger,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "700",
                  fontSize: "1rem",
                  cursor: "pointer",
                  transition: ANIMATIONS.hover,
                  boxShadow: SHADOWS.sm
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#dc2626";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = SHADOWS.md;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = COLORS.danger;
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = SHADOWS.sm;
                }}
              >
                Reject
              </button>
            </div>
          )}
          
          {/* Show current vote status */}
          {anteVotes.hasOwnProperty(player?.playerID) && anteVotes[player?.playerID] !== null && (
            <div style={{ 
              fontSize: "1rem", 
              opacity: 0.9, 
              marginTop: "8px",
              fontWeight: "600",
              color: anteVotes[player?.playerID] === "accept" ? COLORS.success : COLORS.danger
            }}>
              You voted: {anteVotes[player?.playerID] === "accept" ? "Accept" : "Reject"}
            </div>
          )}
          
          {/* Show cancel button for requester */}
          {anteRequest.requesterID === player?.playerID && state.autoAcceptCountdown === undefined && (
            <button
              onClick={handleCancelAnteRequest}
              style={{
                padding: "8px 16px",
                background: COLORS.textMuted,
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontWeight: "600",
                fontSize: "0.875rem",
                cursor: "pointer",
                transition: ANIMATIONS.hover,
                marginTop: "16px"
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#6b7280";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = COLORS.textMuted;
              }}
            >
              Cancel Request
            </button>
          )}
        </div>
      )}

      {/* Ante Vote Result Notice */}
      {anteVoteComplete && anteVoteResult && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "40%",
          transform: "translate(-50%, -50%)",
          maxWidth: 400,
          background: anteVoteResult === "accepted" ? COLORS.success : COLORS.danger,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${anteVoteResult === "accepted" ? COLORS.success : COLORS.danger}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "24px 32px",
          zIndex: 1001,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            {anteVoteResult === "accepted" ? "✅" : "❌"}
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            {anteVoteResult === "accepted" ? "Ante Change Accepted!" : "Ante Change Rejected"}
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
            {anteVoteResult === "accepted" 
              ? `Next round will have ${anteRequest?.requestedAmount || 2} chip ante ($${((anteRequest?.requestedAmount || 2) * creditValue).toFixed(2)})`
              : "Ante will remain at 1 chip for next round"
            }
          </div>
        </div>
      )}

      {/* Credit Increase Request Voting Interface */}
      {creditIncreaseRequest && !creditIncreaseVoteComplete && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 500,
          background: COLORS.surface,
          color: COLORS.text,
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.border}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "32px 40px",
          zIndex: 1002,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            💰
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            Credit Increase Request
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4", marginBottom: "16px" }}>
            {(() => {
              const increaseAmount = creditIncreaseRequest.requestedAmount;
              const affectedPlayers = creditIncreaseRequest.affectedPlayers || [];
              const affectedPlayerNames = affectedPlayers.map(playerID => {
                const player = allSeats.find(p => p.playerID === playerID);
                return player ? player.name : playerID;
              }).join(", ");
              
              return `Some players have insufficient credits to cover both the ante and potential knock loss.\n\nIncrease all players' credits by ${increaseAmount} chips ($${(increaseAmount * creditValue).toFixed(2)})?\n\nAffected players: ${affectedPlayerNames}`;
            })()}
          </div>
          
          {/* Show voting buttons only if this player can vote */}
          {creditIncreaseVotes.hasOwnProperty(player?.playerID) && creditIncreaseVotes[player?.playerID] === null && (
            <div style={{ display: "flex", gap: "16px", marginTop: "16px" }}>
              <button
                onClick={() => handleCreditIncreaseVote("accept")}
                style={{
                  padding: "12px 24px",
                  background: COLORS.success,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "700",
                  fontSize: "1rem",
                  cursor: "pointer",
                  transition: ANIMATIONS.hover,
                  boxShadow: SHADOWS.sm
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#059669";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = SHADOWS.md;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = COLORS.success;
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = SHADOWS.sm;
                }}
              >
                Accept
              </button>
                             <button
                 onClick={() => handleCreditIncreaseVote("reject")}
                 style={{
                   padding: "12px 24px",
                   background: COLORS.danger,
                   color: "white",
                   border: "none",
                   borderRadius: "8px",
                   fontWeight: "700",
                   fontSize: "1rem",
                   cursor: "pointer",
                   transition: ANIMATIONS.hover,
                   boxShadow: SHADOWS.sm
                 }}
                 onMouseEnter={(e) => {
                   e.target.style.background = "#dc2626";
                   e.target.style.transform = "translateY(-1px)";
                   e.target.style.boxShadow = SHADOWS.md;
                 }}
                 onMouseLeave={(e) => {
                   e.target.style.background = COLORS.danger;
                   e.target.style.transform = "translateY(0)";
                   e.target.style.boxShadow = SHADOWS.sm;
                 }}
               >
                 Reject Request and Remove User
               </button>
            </div>
          )}
          
          {/* Show current vote status */}
          {creditIncreaseVotes.hasOwnProperty(player?.playerID) && creditIncreaseVotes[player?.playerID] !== null && (
            <div style={{ 
              fontSize: "1rem", 
              opacity: 0.9, 
              marginTop: "8px",
              fontWeight: "600",
              color: creditIncreaseVotes[player?.playerID] === "accept" ? COLORS.success : COLORS.danger
            }}>
              You voted: {creditIncreaseVotes[player?.playerID] === "accept" ? "Accept" : "Reject"}
            </div>
          )}
        </div>
      )}

      {/* Credit Increase Vote Result Notice */}
      {creditIncreaseVoteComplete && creditIncreaseVoteResult && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "30%",
          transform: "translate(-50%, -50%)",
          maxWidth: 400,
          background: creditIncreaseVoteResult === "accepted" ? COLORS.success : COLORS.danger,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${creditIncreaseVoteResult === "accepted" ? COLORS.success : COLORS.danger}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "24px 32px",
          zIndex: 1003,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            {creditIncreaseVoteResult === "accepted" ? "✅" : "❌"}
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            {creditIncreaseVoteResult === "accepted" ? "Credit Increase Accepted!" : "Credit Increase Rejected"}
          </div>
                     <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
             {creditIncreaseVoteResult === "accepted" 
               ? `All players' credits increased by ${creditIncreaseRequest?.requestedAmount || 0} chips ($${((creditIncreaseRequest?.requestedAmount || 0) * creditValue).toFixed(2)})`
               : "Players without sufficient credits have been removed from the game"
             }
           </div>
        </div>
      )}

        {/* Header Section - Sidebar */}
        <div>
          <h1 style={{ 
            margin: 0, 
            fontSize: "1.25rem",
            fontWeight: "700",
            color: COLORS.text,
            marginBottom: "8px"
          }}>
            Guts Table
          </h1>
          <div style={{ 
            fontSize: "0.75rem",
            color: COLORS.textLight,
            marginBottom: "8px"
          }}>
            <span style={{ fontWeight: "600", display: "block", marginBottom: "2px", fontSize: "0.7rem" }}>Table Code:</span>
            <code style={{ 
              fontFamily: "monospace", 
              fontSize: "0.7rem", 
              color: COLORS.primary,
              background: "#eff6ff",
              padding: "3px 6px",
              borderRadius: "4px",
              border: `1px solid ${COLORS.primary}20`,
              display: "block",
              width: "100%",
              textAlign: "center"
            }}>
              {tableCode}
            </code>
          </div>
        </div>

        {/* Pot and Credit Display - Sidebar */}
        <div style={{
          padding: "8px",
          background: "#f8fafc",
          borderRadius: "8px",
          border: `1px solid ${COLORS.border}`
        }}>
          <div style={{
            background: COLORS.success,
            color: "white",
            padding: "6px 8px",
            borderRadius: "6px",
            fontWeight: "600",
            fontSize: "0.75rem",
            marginBottom: "6px",
            textAlign: "center"
          }}>
            Pot: {pot} chips (${(pot * creditValue).toFixed(2)})
          </div>
          {anteCollected && (
            <div style={{
              background: COLORS.warning,
              color: "white",
              padding: "4px 8px",
              borderRadius: "4px",
              fontWeight: "500",
              fontSize: "0.7rem",
              textAlign: "center",
              marginBottom: "6px"
            }}>
              Ante Collected
            </div>
          )}
          {player && !player.isSpectator && (
            <div style={{
              background: COLORS.primary,
              color: "white",
              padding: "6px 8px",
              borderRadius: "6px",
              fontWeight: "600",
              fontSize: "0.75rem",
              textAlign: "center"
            }}>
              My Credits: {credits[player.playerID] || 0} (${((credits[player.playerID] || 0) * creditValue).toFixed(2)})
            </div>
          )}
        </div>

        {/* Game Status - Sidebar */}
        <div style={{ 
          padding: "8px",
          background: "#f8fafc",
          borderRadius: "8px",
          border: `1px solid ${COLORS.border}`
        }}>
          {phase !== "waiting" && (
            <div style={{
              padding: "4px 8px",
              background: "#eff6ff",
              color: COLORS.primary,
              borderRadius: "12px",
              fontSize: "0.7rem",
              fontWeight: "600",
              border: `1px solid ${COLORS.primary}20`,
              textAlign: "center",
              marginBottom: "6px"
            }}>
              Round {round} - {PHASE_LABELS[phase] || phase}
            </div>
          )}
          {(dealerSuit && typeof dealerIdx === 'number' && allSeats[dealerIdx]) && (
            <div style={{ display: "flex", flexDirection: "column", gap: "3px", fontSize: "0.7rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "center" }}>
                <span style={{ color: COLORS.textLight }}>Dealer Suit:</span>
                <span style={{ 
                  fontSize: "0.9rem", 
                  fontWeight: "700",
                  color: suitColors[dealerSuit]
                }}>
                  {suitSymbols[dealerSuit]}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "center" }}>
                <span style={{ color: COLORS.textLight }}>Dealer:</span>
                <span style={{ 
                  fontWeight: "600",
                  color: COLORS.primary
                }}>
                  {allSeats[dealerIdx].name}
                </span>
              </div>
            </div>
          )}
        </div>

        {isSpectate && (
          <div style={{
            padding: "4px 8px",
            background: "#fef3c7",
            color: "#92400e",
            border: "1px solid #fbbf24",
            borderRadius: "6px",
            fontWeight: "600",
            fontSize: "0.7rem",
            textAlign: "center"
          }}>
            Spectator Mode
          </div>
        )}

        {/* Action Buttons - Sidebar */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <button
            onClick={() => setShowInviteModal(true)}
            style={{
              padding: "6px 10px",
              background: COLORS.primary,
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontWeight: "600",
              fontSize: "0.75rem",
              cursor: "pointer",
              transition: ANIMATIONS.hover,
              boxShadow: SHADOWS.sm,
              width: "100%"
            }}
            onMouseEnter={(e) => e.target.style.background = COLORS.primaryHover}
            onMouseLeave={(e) => e.target.style.background = COLORS.primary}
          >
            Share Invite
          </button>
          
          {isHost && !isSpectate && (
            <button
              onClick={handleAddBot}
              disabled={allSeats.length >= 7 || (phase && phase !== "waiting" && phase !== "results")}
              style={{
                padding: "6px 10px",
                background: (allSeats.length >= 7 || (phase && phase !== "waiting" && phase !== "results")) ? COLORS.textMuted : COLORS.success,
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontWeight: "600",
                fontSize: "0.75rem",
                cursor: (allSeats.length >= 7 || (phase && phase !== "waiting" && phase !== "results")) ? "not-allowed" : "pointer",
                transition: ANIMATIONS.hover,
                boxShadow: SHADOWS.sm,
                width: "100%"
              }}
              onMouseEnter={(e) => {
                if (allSeats.length < 7 && (!phase || phase === "waiting" || phase === "results")) {
                  e.target.style.background = "#059669";
                }
              }}
              onMouseLeave={(e) => {
                if (allSeats.length < 7 && (!phase || phase === "waiting" || phase === "results")) {
                  e.target.style.background = COLORS.success;
                }
              }}
            >
              Add Bot
            </button>
          )}
          
          {/* Ante Request Interface */}
          {!isSpectate && (phase === "waiting" || phase === "results") && !anteRequest && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px"
            }}>
              <select
                value={selectedAnteAmount}
                onChange={(e) => setSelectedAnteAmount(parseInt(e.target.value))}
                style={{
                  padding: "4px 6px",
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: "4px",
                  fontSize: "0.7rem",
                  background: "white",
                  color: COLORS.text,
                  fontWeight: "600",
                  width: "100%"
                }}
              >
                {Array.from({length: 20}, (_, i) => i + 1).map(num => (
                  <option key={num} value={num}>
                    {num} chip{num !== 1 ? 's' : ''} (${(num * creditValue).toFixed(2)})
                  </option>
                ))}
              </select>
              <button
                onClick={() => handleRequestAnteChange(selectedAnteAmount)}
                style={{
                  padding: "6px 10px",
                  background: COLORS.warning,
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: "600",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  transition: ANIMATIONS.hover,
                  boxShadow: SHADOWS.sm,
                  width: "100%"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#d97706";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = SHADOWS.md;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = COLORS.warning;
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = SHADOWS.sm;
                }}
              >
                Request Ante Change
              </button>
            </div>
          )}
          
          <button
            onClick={() => setShowChat(!showChat)}
            style={{
              background: showChat ? COLORS.secondary : COLORS.primary,
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "6px 10px",
              fontWeight: "700",
              fontSize: "0.75rem",
              cursor: "pointer",
              transition: ANIMATIONS.hover,
              boxShadow: SHADOWS.sm,
              width: "100%"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = showChat ? "#475569" : COLORS.primaryHover;
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = SHADOWS.md;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = showChat ? COLORS.secondary : COLORS.primary;
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = SHADOWS.sm;
            }}
          >
            {showChat ? "Hide Chat" : "Show Chat"}
          </button>
          
          <button
            style={{ 
              background: COLORS.success, 
              color: 'white', 
              border: 'none', 
              borderRadius: "6px", 
              padding: '6px 10px', 
              fontWeight: '700',
              fontSize: "0.75rem",
              cursor: "pointer",
              transition: ANIMATIONS.hover,
              boxShadow: SHADOWS.sm,
              width: "100%"
            }}
            onClick={handleGeneratePaymentReport}
            onMouseEnter={(e) => {
              e.target.style.background = "#059669";
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = SHADOWS.md;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = COLORS.success;
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = SHADOWS.sm;
            }}
          >
            Payment Report
          </button>

          <button
            type="button"
            onClick={openGameHelp}
            style={{
              background: COLORS.secondary,
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "6px 10px",
              fontWeight: "700",
              fontSize: "0.75rem",
              cursor: "pointer",
              transition: ANIMATIONS.hover,
              boxShadow: SHADOWS.sm,
              width: "100%",
            }}
          >
            Help
          </button>
          
          <button
            style={{ 
              background: COLORS.danger, 
              color: 'white', 
              border: 'none', 
              borderRadius: "6px", 
              padding: '6px 10px', 
              fontWeight: '700',
              fontSize: "0.75rem",
              cursor: "pointer",
              transition: ANIMATIONS.hover,
              boxShadow: SHADOWS.sm,
              width: "100%"
            }}
            onClick={handleTakeBreak}
            onMouseEnter={(e) => {
              e.target.style.background = "#dc2626";
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = SHADOWS.md;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = COLORS.danger;
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = SHADOWS.sm;
            }}
          >
            Take Break
          </button>
          <button
            type="button"
            onClick={handleQuitPermanent}
            style={{
              background: "transparent",
              color: COLORS.textMuted,
              border: "none",
              borderRadius: "6px",
              padding: "4px 10px",
              fontWeight: 500,
              fontSize: "0.68rem",
              cursor: "pointer",
              width: "100%",
              marginTop: 4,
            }}
          >
            Leave permanently
          </button>
        </div>

        {/* Tabs - Sidebar */}
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: "2px",
          background: COLORS.surface,
          borderRadius: "8px",
          padding: "2px",
          boxShadow: SHADOWS.sm,
          border: `1px solid ${COLORS.border}`
        }}>
          <button 
            onClick={() => { setHistoryTab(false); setPaymentTab(false); }} 
            style={{
              fontSize: "0.7rem", 
              fontWeight: '600', 
              padding: "6px 10px", 
              borderRadius: "6px",
              border: 'none',
              background: (!historyTab && !paymentTab) ? COLORS.primary : 'transparent',
              color: (!historyTab && !paymentTab) ? 'white' : COLORS.text,
              cursor: "pointer",
              transition: ANIMATIONS.hover,
              width: "100%",
              textAlign: "left"
            }}
            onMouseEnter={(e) => {
              if (historyTab || paymentTab) {
                e.target.style.background = "#f1f5f9";
              }
            }}
            onMouseLeave={(e) => {
              if (historyTab || paymentTab) {
                e.target.style.background = 'transparent';
              }
            }}
          >
            Game Table
          </button>
          <button 
            onClick={() => { setHistoryTab(true); setPaymentTab(false); }} 
            style={{
              fontSize: "0.7rem", 
              fontWeight: '600', 
              padding: "6px 10px", 
              borderRadius: "6px",
              border: 'none',
              background: historyTab ? COLORS.primary : 'transparent',
              color: historyTab ? 'white' : COLORS.text,
              cursor: "pointer",
              transition: ANIMATIONS.hover,
              width: "100%",
              textAlign: "left"
            }}
            onMouseEnter={(e) => {
              if (!historyTab) {
                e.target.style.background = "#f1f5f9";
              }
            }}
            onMouseLeave={(e) => {
              if (!historyTab) {
                e.target.style.background = 'transparent';
              }
            }}
          >
            Hand History
          </button>
          <button 
            onClick={() => { setHistoryTab(false); setPaymentTab(true); }} 
            style={{
              fontSize: "0.7rem", 
              fontWeight: '600', 
              padding: "6px 10px", 
              borderRadius: "6px",
              border: 'none',
              background: paymentTab ? COLORS.primary : 'transparent',
              color: paymentTab ? 'white' : COLORS.text,
              cursor: "pointer",
              transition: ANIMATIONS.hover,
              width: "100%",
              textAlign: "left"
            }}
            onMouseEnter={(e) => {
              if (!paymentTab) {
                e.target.style.background = "#f1f5f9";
              }
            }}
            onMouseLeave={(e) => {
              if (!paymentTab) {
                e.target.style.background = 'transparent';
              }
            }}
          >
            Payment Tracking
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        padding: "20px",
        overflowY: "auto",
        minWidth: 0,
        position: "relative"
      }}>
      {/* Suit Voting */}
      {phase === "suitvote" && (
        <div style={{ 
          textAlign: 'center', 
          margin: "16px auto 24px auto", 
          padding: "16px", 
          background: COLORS.surface,
          borderRadius: "12px",
          boxShadow: SHADOWS.md,
          border: `1px solid ${COLORS.border}`,
          maxWidth: 320,
          position: "relative",
          zIndex: 10
        }}>
          <div style={{ 
            fontWeight: "700", 
            fontSize: "1rem", 
            marginBottom: "12px",
            color: COLORS.text
          }}>
            Vote for Dealer Suit
          </div>
          <div style={{ 
            display: "flex", 
            justifyContent: "center", 
            gap: "8px",
            marginBottom: "12px"
          }}>
            {SUIT_KEYS.map(suitKey => {
              const chosen = (suitVotes || []).find(v => v.playerID === player.playerID && v.suit === suitKey);
              const isDisabled = (suitVotes || []).some(v => v.playerID === player.playerID);
              return (
                <button
                  key={suitKey}
                  disabled={isDisabled}
                  onClick={() => handleSuitVote(suitKey)}
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "700",
                    background: chosen ? "#eff6ff" : COLORS.surface,
                    color: suitKey === "hearts" || suitKey === "diamonds" ? "#dc2626" : COLORS.text,
                    border: chosen ? `2px solid ${COLORS.primary}` : `2px solid ${COLORS.border}`,
                    borderRadius: "8px",
                    padding: "12px",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    transition: ANIMATIONS.hover,
                    minWidth: "50px",
                    minHeight: "50px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: chosen ? SHADOWS.md : SHADOWS.sm
                  }}
                  onMouseEnter={(e) => {
                    if (!isDisabled) {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = SHADOWS.lg;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDisabled) {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = chosen ? SHADOWS.md : SHADOWS.sm;
                    }
                  }}
                >
                  {suitSymbols[suitKey]}
                </button>
              );
            })}
          </div>
          <div style={{ 
            display: "flex", 
            flexWrap: "wrap", 
            justifyContent: "center", 
            gap: "6px"
          }}>
            {allSeats.map(p =>
              <div key={p.playerID} style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "4px",
                padding: "4px 8px",
                background: (suitVotes || []).some(v => v.playerID === p.playerID) ? "#f0fdf4" : "#f8fafc",
                borderRadius: "12px",
                border: `1px solid ${(suitVotes || []).some(v => v.playerID === p.playerID) ? COLORS.success : COLORS.border}`
              }}>
                <span style={{ 
                  color: (suitVotes || []).some(v => v.playerID === p.playerID) ? COLORS.success : COLORS.textMuted,
                  fontWeight: "600",
                  fontSize: "0.75rem"
                }}>
                  {(suitVotes || []).some(v => v.playerID === p.playerID) ? "✓" : "○"}
                </span>
                <span style={{ 
                  fontSize: "0.75rem",
                  color: COLORS.text,
                  fontWeight: "500"
                }}>
                  {p.name}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dealer Reveal Status */}
      {phase === "dealer_reveal" && (
        <div style={{ 
          width: "100%", 
          textAlign: "center", 
          margin: "16px 0 24px 0", 
          padding: "16px",
          background: COLORS.surface,
          borderRadius: "12px",
          boxShadow: SHADOWS.sm,
          border: `1px solid ${COLORS.border}`,
          position: "relative",
          zIndex: 10
        }}>
          <div style={{ 
            fontWeight: "700", 
            fontSize: "1.125rem", 
            marginBottom: "8px",
            color: COLORS.text
          }}>
            Finding Dealer...{" "}
            {dealerSuit ? `[${suitSymbols[dealerSuit]}]` : ""}
          </div>
          {dealerRevealFound == null && (
            <div style={{ 
              color: COLORS.textLight, 
              fontWeight: "600", 
              fontSize: "0.875rem",
              marginBottom: "8px"
            }}>
              Cards are being revealed one by one...
            </div>
          )}
          {dealerRevealFound != null && dealerRevealDone && (
            <div style={{
              fontSize: "1rem", 
              fontWeight: "700", 
              color: COLORS.text
            }}>
              Dealer Found: <span style={{ 
                fontSize: "1.125rem", 
                color: COLORS.primary, 
                fontWeight: "700" 
              }}>
                {allSeats[dealerRevealFound]?.name}
              </span>!
            </div>
          )}
        </div>
      )}

      <div className="guts-tab-content" style={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {historyTab && (
        <div 
          id="history-log-scroll" 
          ref={historyRef}
          style={{ 
            background: COLORS.surface, 
            borderRadius: "16px", 
            minHeight: 300, 
            maxHeight: 400, 
            overflow: 'auto', 
            marginBottom: 24, 
            padding: 24, 
            border: `1px solid ${COLORS.border}`,
            fontSize: 14,
            boxShadow: SHADOWS.md
          }}
        >
          <div style={{ 
            fontWeight: "700", 
            fontSize: "1.25rem",
            marginBottom: "16px",
            color: COLORS.text
          }}>
            Hand History
          </div>
          <div style={{ marginTop: 16 }}>
            {(historyArr && historyArr.length)
              ? historyArr.slice().map((h, i) =>
                <div key={i} style={{ 
                  marginBottom: 20,
                  padding: "16px",
                  background: "#f8fafc",
                  borderRadius: "12px",
                  border: `1px solid ${COLORS.border}`
                }}>
                  <div style={{ 
                    fontWeight: '700', 
                    color: COLORS.primary,
                    fontSize: "1rem",
                    marginBottom: "8px"
                  }}>
                    Round {h.round}:
                  </div>
                  <div style={{ 
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                    fontSize: "0.875rem"
                  }}>
                    <div><strong>Winner{Array.isArray(h.winnerIdx) && h.winnerIdx.length > 1 ? 's' : ''}:</strong> <span style={{ color: COLORS.success }}>{Array.isArray(h.winnerIdx) ? h.winnerIdx.map(idx => allSeats[idx]?.name).join(", ") : allSeats[h.winnerIdx]?.name || "unknown"}</span></div>
                    <div><strong>Dealer:</strong> {allSeats[h.dealerIdx]?.name}</div>
                    <div><strong>Winning Hand:</strong> <span style={{ color: COLORS.primary, fontWeight: "600" }}>{h.winningHand || "Unknown"}</span></div>
                    <div><strong>Hand Type:</strong> <span style={{ color: COLORS.textMuted }}>{h.winningHandType || "Unknown"}</span></div>
                    {h.pot != null && (
                      <div><strong>Winning Pot:</strong> <span style={{ color: COLORS.primary, fontWeight: "600" }}>{h.pot} chips (${(h.pot * creditValue).toFixed(2)})</span></div>
                    )}
                    <div><strong>Knocked:</strong> {h.knocked.map(idx => allSeats[idx]?.name).join(", ")}</div>
                    <div><strong>Discarded:</strong> {(h.discarded || []).map(idx => allSeats[idx]?.name).join(", ") || "(none)"}</div>
                    {h.dealerChanged && (
                      <div style={{ 
                        gridColumn: "1 / -1", 
                        color: COLORS.warning, 
                        fontWeight: "700",
                        fontSize: "0.9rem",
                        marginTop: "4px",
                        padding: "4px 8px",
                        background: "#fef3c7",
                        borderRadius: "6px",
                        border: "1px solid #f59e0b"
                      }}>
                        🎉 Special Win! Dealer changed to {allSeats[h.newDealerIdx]?.name || "unknown"}
                      </div>
                    )}
                  </div>
                </div>
              )
              : <div style={{ 
                  color: COLORS.textMuted,
                  textAlign: "center",
                  padding: "32px",
                  fontSize: "1rem"
                }}>
                  No previous hands yet.
                </div>
            }
          </div>
        </div>
      )}
      {paymentTab && (
        <div 
          style={{ 
            background: COLORS.surface, 
            borderRadius: "16px", 
            minHeight: 300, 
            maxHeight: 400, 
            overflow: 'auto', 
            marginBottom: 24, 
            padding: 24, 
            border: `1px solid ${COLORS.border}`,
            fontSize: 14,
            boxShadow: SHADOWS.md
          }}
        >
          <div style={{ 
            fontWeight: "700", 
            fontSize: "1.25rem",
            marginBottom: "16px",
            color: COLORS.text
          }}>
            Payment Tracking
          </div>
          <div style={{ marginTop: 16 }}>
            {/* Credit Value Display */}
            <div style={{ 
              marginBottom: "16px",
              padding: "12px",
              background: "#f0f9ff",
              borderRadius: "8px",
              border: `1px solid ${COLORS.primary}`,
              textAlign: "center"
            }}>
              <div style={{ fontWeight: "600", color: COLORS.primary, marginBottom: "4px" }}>
                Credit Value: ${creditValue.toFixed(2)} per chip
              </div>
            </div>
            
            {/* Payment Tracking Summary */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ 
                fontWeight: "700", 
                fontSize: "1rem",
                marginBottom: "12px",
                color: COLORS.text
              }}>
                Your Net Payment Summary:
              </div>
              {calculatePaymentTracking && calculatePaymentTracking.length > 0 ? (
                calculatePaymentTracking.map((tracking, i) => (
                  <div key={i} style={{ 
                    marginBottom: "12px",
                    padding: "12px",
                    background: tracking.netAmount > 0 ? "#fef2f2" : "#f0fdf4",
                    borderRadius: "8px",
                    border: `1px solid ${tracking.netAmount > 0 ? "#fecaca" : "#bbf7d0"}`
                  }}>
                    <div style={{ 
                      fontWeight: "600", 
                      fontSize: "0.875rem",
                      marginBottom: "4px"
                    }}>
                      {tracking.playerName}
                    </div>
                    <div style={{ 
                      fontSize: "0.875rem",
                      color: COLORS.textMuted,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4
                    }}>
                      {tracking.youOweThem > 0 && (
                        <strong style={{ color: COLORS.error }}>
                          You owe ${(tracking.youOweThem * creditValue).toFixed(2)}
                        </strong>
                      )}
                      {tracking.theyOweYou > 0 && (
                        <strong style={{ color: COLORS.success }}>
                          Owes you ${(tracking.theyOweYou * creditValue).toFixed(2)}
                        </strong>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ 
                  color: COLORS.textMuted,
                  textAlign: "center",
                  padding: "16px",
                  fontSize: "0.875rem"
                }}>
                  No payment activity yet.
                </div>
              )}
            </div>
            
            {/* Detailed Payment History */}
            <div style={{ 
              fontWeight: "700", 
              fontSize: "1rem",
              marginBottom: "12px",
              color: COLORS.text
            }}>
              Detailed History:
            </div>
            {(paymentHistory && paymentHistory.length)
              ? paymentHistory.slice().reverse().map((payment, i) =>
                <div key={i} style={{ 
                  marginBottom: 16,
                  padding: "12px",
                  background: "#f8fafc",
                  borderRadius: "8px",
                  border: `1px solid ${COLORS.border}`
                }}>
                  <div style={{ 
                    fontWeight: '700', 
                    color: COLORS.primary,
                    fontSize: "0.875rem",
                    marginBottom: "6px"
                  }}>
                    Round {payment.round}:
                  </div>
                  <div style={{ 
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "6px",
                    fontSize: "0.75rem"
                  }}>
                    <div><strong>Type:</strong> <span style={{ color: payment.type === 'win' ? COLORS.success : COLORS.warning, fontWeight: "600" }}>{payment.type === 'win' ? 'Single Winner' : 'Tie'}</span></div>
                    <div><strong>Pot:</strong> <span style={{ color: COLORS.primary, fontWeight: "600" }}>{payment.pot} chips (${(payment.pot * creditValue).toFixed(2)})</span></div>
                    {payment.type === 'win' ? (
                      <>
                        <div><strong>Winner:</strong> <span style={{ color: COLORS.success, fontWeight: "600" }}>{(allSeats || []).find(p => p.playerID === payment.winner)?.name || "Unknown"}</span></div>
                        <div><strong>Won:</strong> <span style={{ color: COLORS.success, fontWeight: "600" }}>{payment.amount} chips (${(payment.amount * creditValue).toFixed(2)})</span></div>
                      </>
                    ) : (
                      <>
                        <div><strong>Tied Players:</strong> <span style={{ color: COLORS.warning, fontWeight: "600" }}>{(payment.players || []).map(id => (allSeats || []).find(p => p.playerID === id)?.name || "Unknown").join(", ")}</span></div>
                        <div><strong>Split:</strong> <span style={{ color: COLORS.warning, fontWeight: "600" }}>{payment.amount} chips each (${(payment.amount * creditValue).toFixed(2)})</span></div>
                      </>
                    )}
                  </div>
                </div>
              )
              : <div style={{ 
                  color: COLORS.textMuted,
                  textAlign: "center",
                  padding: "16px",
                  fontSize: "0.875rem"
                }}>
                  No payment history yet.
                </div>
            }
          </div>
        </div>
      )}
        <div style={{ flex: 1, minHeight: 0, display: historyTab || paymentTab ? "none" : "block" }}>
          {/* Poker Table Container */}
          <div style={{
            position: "relative",
            width: "100%",
            minHeight: "1100px",
            height: "1100px",
            margin: "0 auto 32px auto",
            padding: "200px 20px 180px 20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxSizing: "border-box",
            overflow: "visible"
          }}>
            {/* Central Table */}
            <div style={{
              position: "absolute",
              width: "600px",
              height: "350px",
              background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
              borderRadius: "50%",
              border: `10px solid #047857`,
              boxShadow: SHADOWS.xl,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1
            }}>
              {/* Table Center Content */}
              <div style={{
                textAlign: "center",
                color: "white",
                fontWeight: "700"
              }}>
                <div style={{
                  fontSize: "2rem",
                  marginBottom: "10px"
                }}>
                  GUTS
                </div>
                <div style={{
                  fontSize: "1.25rem",
                  opacity: 0.9
                }}>
                  {phase === "waiting" ? "Waiting for players..." : 
                   phase === "suitvote" ? "Vote for Dealer Suit" :
                   phase === "dealer_reveal" ? "Finding Dealer..." :
                   phase === "deal" ? "Dealing cards..." :
                   phase === "action" ? "Deciding knock/discard..." :
                   phase === "reveal" ? "Revealing knocked hands..." :
                   phase === "results" ? "Hand Complete!" : "Game in Progress"}
                </div>
                {phase !== "waiting" && (
                  <div style={{
                    fontSize: "0.875rem",
                    opacity: 0.8,
                    marginTop: "8px"
                  }}>
                    Round {round}
                  </div>
                )}
              </div>
            </div>

            {/* Player Seats - Poker Table Layout */}
            {(allSeats || []).map((p, idx) => {
              const isWin = (Array.isArray(winnerIdx) ? winnerIdx.includes(idx) : winnerIdx === idx) && (phase === "results" || phase === "done");
              const border = seatBorder(isWin, false);
              const background = seatBackground(isWin, false);
              const isBot = p.isBot;
              const isDealer = dealerIdx === idx;
              const isMe = p.playerID === player.playerID;
              
              // Calculate position around the table - equidistant spacing
              const totalPlayers = allSeats.length;
              const angle = (idx * 2 * Math.PI / totalPlayers) - (Math.PI / 2); // Start from top, equidistant
              
              // Table dimensions
              const tableWidth = 600;
              const tableHeight = 350;
              const tableRadiusX = tableWidth / 2; // 300px
              const tableRadiusY = tableHeight / 2; // 175px
              
              // Placard dimensions
              const placardHeight = 180; // Approximate placard height including padding
              const placardWidth = SEAT_FIXED_WIDTH; // 150px
              
              // Outer ring radius - table edge + clearance
              const outerRingClearance = 20; // Space between table edge and placard
              const outerRadiusX = tableRadiusX + outerRingClearance + placardWidth / 2;
              const outerRadiusY = tableRadiusY + outerRingClearance + placardHeight / 2;
              
              // Calculate position on outer ring (ellipse)
              const seatX = Math.cos(angle) * outerRadiusX;
              const seatY = Math.sin(angle) * outerRadiusY;
              
              // Determine if player is above or below median (center line)
              const isAboveMedian = seatY < 0;
              const isBelowMedian = seatY > 0;
              
              // Adjust transform based on position
              // Below median: top edge aligns with outer ring (translate Y = 0)
              // Above median: bottom edge aligns with outer ring (translate Y = -100%)
              // On median (seatY === 0): center align (translate Y = -50%)
              let transformY = "-50%"; // Default center
              if (isBelowMedian) {
                transformY = "0%"; // Top edge aligns
              } else if (isAboveMedian) {
                transformY = "-100%"; // Bottom edge aligns
              }
              
              return (
                <div
                  key={p.playerID}
                  className={[
                    "seat-card",
                    isWin ? "winner" : ""
                  ].filter(Boolean).join(" ")}
                  style={{
                    position: "absolute",
                    left: `calc(50% + ${seatX}px)`,
                    top: `calc(50% + ${seatY}px)`,
                    transform: `translate(-50%, ${transformY})`,
                    textAlign: "center",
                    width: SEAT_FIXED_WIDTH,
                    minWidth: SEAT_FIXED_WIDTH,
                    maxWidth: SEAT_FIXED_WIDTH,
                    border,
                    borderRadius: "12px",
                    padding: "12px",
                    background,
                    boxSizing: 'border-box',
                    boxShadow: isWin ? SHADOWS.xl : SHADOWS.md,
                    transition: ANIMATIONS.transition,
                    transform: `translate(-50%, -50%) ${isWin ? "scale(1.02)" : "scale(1)"}`,
                    zIndex: isWin ? 10 : 2
                  }}>
                  <img
                    src={displayAvatar(p)}
                    alt={`${p.name}'s avatar`}
                    width={48} height={48}
                    style={{ 
                      borderRadius: '50%', 
                      marginBottom: "8px", 
                      background: COLORS.surface, 
                      border: `2px solid ${COLORS.border}`,
                      boxShadow: SHADOWS.sm
                    }}
                  />
                  <div style={{ 
                    fontWeight: "700", 
                    fontSize: '0.95rem',
                    marginBottom: "6px",
                    color: COLORS.text
                  }}>
                    {p.name}
                    {idx === 0 && <span style={{ 
                      marginLeft: 8, 
                      color: COLORS.warning,
                      fontSize: "1rem"
                    }}>👑</span>}
                  </div>
                  <div style={{ marginBottom: "6px" }}>{playerIndicators(idx, isBot)}</div>
                  
                  {/* Credit Display - Only show for current player */}
                  {isMe && (
                    <div style={{
                      background: COLORS.primary,
                      color: "white",
                      padding: "3px 6px",
                      borderRadius: "4px",
                      fontSize: "0.7rem",
                      fontWeight: "600",
                      marginBottom: "6px"
                    }}>
                      Credits: {credits[p.playerID] || 0} (${((credits[p.playerID] || 0) * creditValue).toFixed(2)})
                    </div>
                  )}
                  
                  {/* Free Hand Indicator */}
                  {freeHands[p.playerID] && (
                    <div style={{
                      background: COLORS.success,
                      color: "white",
                      padding: "3px 6px",
                      borderRadius: "4px",
                      fontSize: "0.7rem",
                      fontWeight: "600",
                      marginBottom: "6px"
                    }}>
                      FREE HAND
                    </div>
                  )}
                  
                  {isBot && isHost && (phase === "waiting" || phase === "results") && (
                    <button
                      onClick={() => socket.emit("removeBot", {
                        tableID: table.id,
                        playerID: player.playerID,
                        botPlayerID: p.playerID,
                      })}
                      title="Remove Bot"
                      style={{
                        position: "absolute", 
                        top: 12, 
                        right: 12,
                        border: "none", 
                        background: COLORS.danger, 
                        color: "white", 
                        fontWeight: '700', 
                        fontSize: "1rem",
                        cursor: "pointer",
                        borderRadius: "50%",
                        width: "28px",
                        height: "28px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: SHADOWS.sm,
                        transition: ANIMATIONS.hover
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "scale(1.1)";
                        e.target.style.boxShadow = SHADOWS.md;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "scale(1)";
                        e.target.style.boxShadow = SHADOWS.sm;
                      }}
                    >
                      ×
                    </button>
                  )}
                  
                  {isBot && (
                    <span style={{ 
                      fontSize: '0.65rem', 
                      color: COLORS.textMuted, 
                      display: 'block',
                      marginBottom: isWin ? 2 : "6px"
                    }}>
                      (Bot)
                    </span>
                  )}
                  
                  {(isDealer || (phase === "dealer_reveal" && dealerRevealFound === idx && dealerRevealDone)) && (
                    <div style={{ 
                      color: COLORS.primary, 
                      fontWeight: '700', 
                      marginBottom: isWin ? 2 : "6px",
                      padding: "3px 8px",
                      background: "#eff6ff",
                      borderRadius: "8px",
                      fontSize: "0.75rem",
                      border: `1px solid ${COLORS.primary}20`
                    }}>
                      DEALER
                    </div>
                  )}
                  
                  {/* Placeholder for non-dealers to maintain uniform card height */}
                  {!(isDealer || (phase === "dealer_reveal" && dealerRevealFound === idx && dealerRevealDone)) && (
                    <div style={{ 
                      marginBottom: isWin ? 2 : "6px",
                      padding: "3px 8px",
                      background: "transparent",
                      borderRadius: "8px",
                      fontSize: "0.75rem",
                      border: "1px solid transparent",
                      height: "22px" // Match the height of the dealer identifier
                    }}>
                    </div>
                  )}
                  
                  {/* Card Display */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: 8, 
                    marginBottom: 16, 
                    marginTop: 8,
                    height: "60px",
                    alignItems: "center"
                  }}>
                    {phase === "dealer_reveal" && lastCardForSeat[idx] ? (
                      <div 
                        className="card-visual dealer-reveal"
                        style={{
                          color: suitColors[lastCardForSeat[idx].suit],
                          background: COLORS.surface,
                          border: `2px solid ${COLORS.border}`,
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "1.5rem",
                          fontWeight: "700",
                          boxShadow: SHADOWS.md,
                          minWidth: "50px",
                          height: "44px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center"
                        }}
                      >
                        {lastCardForSeat[idx].rank}{suitSymbols[lastCardForSeat[idx].suit]}
                      </div>
                    ) : shouldShowCards(idx) && hands && hands[idx]
                      ? hands[idx].map((card, j) => (
                        <div 
                          key={j} 
                          className="card-visual"
                          style={{
                            color: card.suit === 'hearts' || card.suit === 'diamonds' ? "#dc2626" : COLORS.text,
                            background: COLORS.surface,
                            border: `2px solid ${COLORS.border}`,
                            borderRadius: "8px",
                            padding: "8px 12px",
                            fontSize: "1.25rem",
                            fontWeight: "700",
                            boxShadow: SHADOWS.sm,
                            minWidth: "40px",
                            height: "44px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center"
                          }}
                        >
                          {card.rank}{suitSymbols[card.suit]}
                        </div>
                      ))
                      : hands && hands[idx]
                        ? <>
                          <div 
                            className="card-visual hidden"
                            style={{
                              background: COLORS.surface,
                              border: `2px solid ${COLORS.border}`,
                              borderRadius: "8px",
                              padding: "8px 12px",
                              fontSize: "1.25rem",
                              color: COLORS.textMuted,
                              boxShadow: SHADOWS.sm,
                              minWidth: "40px",
                              height: "44px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center"
                            }}
                          >
                            🂠
                          </div>
                          <div 
                            className="card-visual hidden"
                            style={{
                              background: COLORS.surface,
                              border: `2px solid ${COLORS.border}`,
                              borderRadius: "8px",
                              padding: "8px 12px",
                              fontSize: "1.25rem",
                              color: COLORS.textMuted,
                              boxShadow: SHADOWS.sm,
                              minWidth: "40px",
                              height: "44px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center"
                            }}
                          >
                            🂠
                          </div>
                        </>
                        : null
                    }
                  </div>
                  
                  {/* Action Buttons */}
                  <div style={{ 
                    minHeight: isWin ? 0 : 80, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    flexDirection: "column",
                    gap: "8px",
                    marginBottom: isWin ? 0 : "12px"
                  }}>
                    {/* Dealer tag placeholder for uniformity */}
                    {!(isDealer || (phase === "dealer_reveal" && dealerRevealFound === idx && dealerRevealDone)) && (
                      <div style={{ 
                        height: "22px", // Match dealer tag height (4px padding + ~14px font + 4px padding)
                        marginBottom: isWin ? 0 : "12px"
                      }} />
                    )}
                    
                    {phase === "action" && idx === activePlayerIdx && isMe && !isSpectate && (
                      <div style={{ 
                        display: "flex",
                        gap: "8px",
                        width: '100%',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <button 
                          onClick={handleKnock} 
                          style={{ 
                            flex: 1,
                            background: COLORS.success, 
                            border: 'none', 
                            padding: '12px 16px', 
                            borderRadius: "8px", 
                            fontWeight: '700', 
                            fontSize: '1rem',
                            color: "white",
                            cursor: "pointer",
                            transition: ANIMATIONS.hover,
                            boxShadow: SHADOWS.sm
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = SHADOWS.md;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = SHADOWS.sm;
                          }}
                        >
                          Knock
                        </button>
                        <button 
                          onClick={handleDiscard} 
                          style={{ 
                            flex: 1,
                            background: COLORS.danger, 
                            border: 'none', 
                            padding: '12px 16px', 
                            borderRadius: "8px", 
                            fontWeight: '700', 
                            fontSize: '1rem',
                            color: "white",
                            cursor: "pointer",
                            transition: ANIMATIONS.hover,
                            boxShadow: SHADOWS.sm
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = SHADOWS.md;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = SHADOWS.sm;
                          }}
                        >
                          Discard
                        </button>
                      </div>
                    )}
                    
                    {phase === "dealer_reveal" && dealerRevealFound != null && dealerRevealDone && isMe && !isSpectate && (
                      <button
                        onClick={() => {
                          setDealerReadyClicked(true);
                          socket.emit("dealerRevealReady", {
                            tableID: table.id,
                            dealerIdx: dealerRevealFound,
                            playerID: player.playerID,
                          });
                        }}
                        disabled={(readies && readies.includes(player.playerID))}
                        style={{
                          background: (dealerReadyClicked || (readies && readies.includes(player.playerID))) ? COLORS.success : COLORS.primary,
                          color: "white", 
                          padding: "12px 24px", 
                          borderRadius: "8px", 
                          fontWeight: "700",
                          fontSize: "1rem", 
                          border: "none", 
                          width: '100%',
                          cursor: (dealerReadyClicked || (readies && readies.includes(player.playerID))) ? "default" : "pointer",
                          transition: ANIMATIONS.hover,
                          boxShadow: SHADOWS.sm
                        }}
                        onMouseEnter={(e) => {
                          if (!dealerReadyClicked && !(readies && readies.includes(player.playerID))) {
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = SHADOWS.md;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!dealerReadyClicked && !(readies && readies.includes(player.playerID))) {
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = SHADOWS.sm;
                          }
                        }}
                      >
                        {(dealerReadyClicked || (readies && readies.includes(player.playerID))) ? "✓ Ready for Dealing!" : "Ready for Dealing"}
                      </button>
                    )}
                    
                    {phase === "waiting" && isMe && !isSpectate && isFirstHand && (
                      <button
                        disabled={readyList.includes(player.playerID)}
                        style={{
                          background: readyList.includes(player.playerID) ? COLORS.success : COLORS.primary,
                          color: "white", 
                          padding: "12px 24px", 
                          borderRadius: "8px", 
                          fontWeight: "700",
                          fontSize: "1rem", 
                          border: "none", 
                          width: '100%',
                          cursor: readyList.includes(player.playerID) ? "default" : "pointer",
                          transition: ANIMATIONS.hover,
                          boxShadow: SHADOWS.sm
                        }}
                        onClick={handleReadyForSuitVote}
                        onMouseEnter={(e) => {
                          if (!readyList.includes(player.playerID)) {
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = SHADOWS.md;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!readyList.includes(player.playerID)) {
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = SHADOWS.sm;
                          }
                        }}
                      >
                        {readyList.includes(player.playerID) ? "✓ Ready" : "Ready to Play"}
                      </button>
                    )}
                    
                    {phase === "results" && isMe && !isSpectate && (
                      <button
                        disabled={resultReadies.includes(player.playerID)}
                        style={{
                          background: resultReadies.includes(player.playerID) ? COLORS.success : COLORS.primary,
                          color: "white", 
                          padding: "12px 24px", 
                          borderRadius: "8px",
                          fontWeight: "700", 
                          fontSize: "1rem", 
                          border: "none", 
                          width: '100%',
                          cursor: resultReadies.includes(player.playerID) ? "default" : "pointer",
                          transition: ANIMATIONS.hover,
                          boxShadow: SHADOWS.sm
                        }}
                        onClick={handleNextRoundReady}
                        onMouseEnter={(e) => {
                          if (!resultReadies.includes(player.playerID)) {
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = SHADOWS.md;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!resultReadies.includes(player.playerID)) {
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = SHADOWS.sm;
                          }
                        }}
                      >
                        {resultReadies.includes(player.playerID) ? "✓ Ready" : "Ready for Next Round"}
                      </button>
                    )}
                  </div>
                  
                  {/* Winner Badge */}
                  {isWin && (
                    <div
                      style={{
                        marginTop: 8,
                        fontWeight: '700',
                        color: '#92400e',
                        fontSize: "1.25rem",
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        border: `2px solid ${COLORS.warning}`,
                        borderRadius: "12px",
                        boxShadow: SHADOWS.lg,
                        padding: '12px 20px',
                        letterSpacing: '0.5px',
                        textAlign: "center"
                      }}
                    >
                      👑 WINNER!
                    </div>
                  )}
                  
                  {/* Winner Earnings Display */}
                  {isWin && (() => {
                    console.log(`DEBUG - Client side winnerEarnings: ${table.state.winnerEarnings}, isWin: ${isWin}, phase: ${table.state.phase}`);
                    return (
                      <div style={{ 
                        marginTop: 4,
                        color: "#10b981", 
                        fontWeight: '700', 
                        padding: "8px 12px",
                        background: "#fef3c7",
                        borderRadius: "12px",
                        fontSize: "0.875rem",
                        border: `1px solid #fbbf24`,
                        textAlign: "center"
                      }}>
                        Won +{table.state.winnerEarnings || 0} chips (${((table.state.winnerEarnings || 0) * (table.state.creditValue || 1.00)).toFixed(2)})
                      </div>
                    );
                  })()}
                  
                  {/* Ready Status */}
                  {(phase === "waiting" || phase === "results") && (
                    <div style={{
                      marginTop: isWin ? 8 : 12,
                      fontSize: "0.875rem",
                      color: (phase === "waiting"
                        ? (readyList.includes(p.playerID) ? COLORS.success : COLORS.textMuted)
                        : (resultReadies.includes(p.playerID) ? COLORS.success : COLORS.textMuted)),
                      fontWeight: "600"
                    }}>
                      {(phase === "waiting"
                        ? (readyList.includes(p.playerID) ? "✓ Ready" : "Not Ready")
                        : (resultReadies.includes(p.playerID) ? "✓ Ready" : "Not Ready"))
                      }
                    </div>
                  )}
                  
                  {/* Ante Display */}
                  {(() => {
                    const roundContributions = table.state?.roundContributions || {};
                    const playerAnte = roundContributions[p.playerID] || 0;
                    if (playerAnte > 0 || (phase === "deal" && !anteCollected)) {
                      const anteDollar = (playerAnte * (table.state?.creditValue || 1.00)).toFixed(2);
                      return (
                        <div style={{ 
                          background: COLORS.primary,
                          color: "white",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          marginTop: isWin ? 4 : "8px",
                          marginBottom: "8px"
                        }}>
                          Ante - {playerAnte} credits (${anteDollar})
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {(historyTab || paymentTab) && playerNeedsAction && (
        <div style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: showChat ? 320 : 0,
          zIndex: 2000,
          background: "#1e293b",
          color: "#fff",
          padding: "12px 16px",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.25)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}>
          <div style={{ flex: 1, minWidth: 180, fontWeight: 600, fontSize: "0.9rem" }}>
            Action required — respond to continue the game
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {phase === "action" && mySeatIdx === activePlayerIdx && (
              <>
                <button onClick={handleKnock} style={{ background: "#166534", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Knock</button>
                <button onClick={handleDiscard} style={{ background: "#7f1d1d", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Discard</button>
              </>
            )}
            {phase === "dealer_reveal" && dealerRevealFound != null && dealerRevealDone && (
              <button
                onClick={() => {
                  setDealerReadyClicked(true);
                  socket.emit("dealerRevealReady", { tableID: table.id, dealerIdx: dealerRevealFound, playerID: player.playerID });
                }}
                disabled={(readies && readies.includes(player.playerID))}
                style={{ background: (readies && readies.includes(player.playerID)) ? "#166534" : "#854d0e", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
              >
                {(readies && readies.includes(player.playerID)) ? "Ready!" : "Ready for Dealing"}
              </button>
            )}
            {phase === "waiting" && isFirstHand && (
              <button
                disabled={readyList.includes(player.playerID)}
                onClick={handleReadyForSuitVote}
                style={{ background: readyList.includes(player.playerID) ? "#166534" : "#854d0e", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
              >
                {readyList.includes(player.playerID) ? "Ready!" : "Ready to Play"}
              </button>
            )}
            {phase === "results" && (
              <button
                disabled={resultReadies.includes(player.playerID)}
                onClick={handleNextRoundReady}
                style={{ background: resultReadies.includes(player.playerID) ? "#166534" : "#854d0e", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
              >
                {resultReadies.includes(player.playerID) ? "Ready!" : "Next Round"}
              </button>
            )}
            <button
              onClick={() => { setHistoryTab(false); setPaymentTab(false); }}
              style={{ background: "transparent", color: "#93c5fd", border: "1px solid #93c5fd", padding: "8px 16px", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}
            >
              Back to table
            </button>
          </div>
        </div>
      )}

      {spectators.length > 0 && (
        <div style={{
          background: COLORS.surface,
          borderRadius: "16px",
          padding: "20px",
          marginBottom: "24px",
          boxShadow: SHADOWS.md,
          border: `1px solid ${COLORS.border}`
        }}>
          <div style={{
            fontWeight: "700",
            fontSize: "1.125rem",
            marginBottom: "16px",
            color: COLORS.text
          }}>
            Spectators ({spectators.length})
          </div>
          <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px"
          }}>
            {spectators.map(s => (
              <div 
                key={s.playerID}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  background: "#f8fafc",
                  borderRadius: "20px",
                  border: `1px solid ${COLORS.border}`,
                  fontSize: "0.875rem"
                }}
              >
                <img 
                  src={displayAvatar(s)} 
                  alt="spectator" 
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    border: `1px solid ${COLORS.border}`
                  }}
                />
                <span style={{
                  padding: "2px 8px",
                  background: "#fef3c7",
                  color: "#92400e",
                  borderRadius: "12px",
                  fontSize: "0.75rem",
                  fontWeight: "600"
                }}>
                  Spectator
                </span>
                <span style={{ 
                  fontSize: "0.875rem",
                  color: COLORS.text,
                  fontWeight: "500"
                }}>
                  {s.name}
                </span>
                {isHost && (
                  <button
                    title="Remove Spectator"
                    onClick={() => handleRemoveSpectator(s.playerID)}
                    style={{
                      background: "none", 
                      border: "none",
                      color: COLORS.danger, 
                      fontSize: "1rem",
                      cursor: "pointer", 
                      fontWeight: "700", 
                      lineHeight: "1",
                      padding: "2px",
                      borderRadius: "4px",
                      transition: ANIMATIONS.hover
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#fef2f2";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "none";
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>

      {/* Side Chat Panel */}
      {showChat && (
        <div style={{
          position: "fixed",
          bottom: 0,
          right: 0,
          width: "320px",
          height: "50vh",
          background: COLORS.surface,
          borderLeft: `1px solid ${COLORS.border}`,
          borderTop: `1px solid ${COLORS.border}`,
          boxShadow: SHADOWS.lg,
          display: "flex",
          flexDirection: "column",
          zIndex: 1000
        }}>
          <div style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: COLORS.primary,
            color: "white"
          }}>
            <div style={{
              fontWeight: "700",
              fontSize: "1.125rem"
            }}>
              Table Chat
            </div>
            <button
              onClick={() => setShowChat(false)}
              style={{
                background: "none",
                border: "none",
                color: "white",
                fontSize: "1.5rem",
                cursor: "pointer",
                padding: "4px",
                borderRadius: "4px",
                transition: ANIMATIONS.hover
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgba(255,255,255,0.1)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "none";
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{
            flex: 1,
            overflow: "auto",
            padding: "16px",
            background: "#f8fafc"
          }}>
            {chat && chat.length
              ? chat.map((msg, i) =>
                <div key={i}
                  style={{ 
                    marginBottom: 8,
                    padding: "8px 12px",
                    background: msg.system ? "#fef3c7" : COLORS.surface,
                    borderRadius: "8px",
                    border: `1px solid ${msg.system ? "#fbbf24" : COLORS.border}`,
                    fontSize: "0.875rem"
                  }}>
                  <div style={{ 
                    color: COLORS.textMuted, 
                    fontSize: "0.75rem",
                    marginBottom: "4px"
                  }}>
                    {msg.timestamp ? msg.timestamp.slice(11, 19) : ''}
                  </div>
                  {msg.system
                    ? <span style={{ color: "#92400e", fontWeight: "600" }}>{msg.text}</span>
                    : <span><strong style={{ color: COLORS.primary }}>{msg.name || "Player"}:</strong> {msg.text}</span>
                  }
                </div>)
              : <div style={{ 
                  color: COLORS.textMuted,
                  textAlign: "center",
                  padding: "32px",
                  fontSize: "0.875rem"
                }}>
                  No chat yet
                </div>
            }
            <div ref={chatListEnd} />
          </div>
          
          {isSpectate ? (
            <div style={{ 
              fontSize: "0.875rem", 
              color: COLORS.textMuted,
              textAlign: "center",
              padding: "12px",
              background: "#f8fafc",
              borderTop: `1px solid ${COLORS.border}`
            }}>
              Spectators cannot chat
            </div>
          ) : (
            <form 
              onSubmit={e => { e.preventDefault(); sendChat(); }} 
              style={{
                padding: "16px",
                borderTop: `1px solid ${COLORS.border}`,
                background: COLORS.surface
              }}
            >
              <div style={{
                display: "flex",
                gap: "8px"
              }}>
                <input
                  value={chatMsg}
                  onChange={e => setChatMsg(e.target.value)}
                  placeholder="Type a message..."
                  maxLength={120}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    outline: "none",
                    transition: ANIMATIONS.hover
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = COLORS.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${COLORS.primary}20`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = COLORS.border;
                    e.target.style.boxShadow = "none";
                  }}
                />
                <button 
                  type="submit" 
                  disabled={!chatMsg.trim()}
                  style={{
                    padding: "12px 16px",
                background: chatMsg.trim() ? COLORS.primary : COLORS.textMuted,
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "0.875rem",
                cursor: chatMsg.trim() ? "pointer" : "not-allowed",
                transition: ANIMATIONS.hover
              }}
              onMouseEnter={(e) => {
                if (chatMsg.trim()) {
                  e.target.style.background = COLORS.primaryHover;
                  e.target.style.transform = "translateY(-1px)";
                }
              }}
              onMouseLeave={(e) => {
                if (chatMsg.trim()) {
                  e.target.style.background = COLORS.primary;
                  e.target.style.transform = "translateY(0)";
                }
              }}
            >
              Send
            </button>
               </div>
             </form>
           )}
         </div>
       )}

      {/* Footer */}
      <div style={{ 
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 0"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {isSpectate && (
            <span style={{ 
              color: COLORS.textMuted, 
              fontWeight: '600',
              fontSize: "0.875rem",
              padding: "6px 12px",
              background: "#fef3c7",
              borderRadius: "20px",
              border: `1px solid #fbbf24`
            }}>
              Spectator Mode
            </span>
          )}
        </div>

      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            background: COLORS.surface,
            borderRadius: "16px",
            padding: "32px",
            maxWidth: "500px",
            width: "90%",
            boxShadow: SHADOWS.xl,
            border: `1px solid ${COLORS.border}`
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px"
            }}>
              <h3 style={{
                margin: 0,
                fontSize: "1.5rem",
                fontWeight: "700",
                color: COLORS.text
              }}>
                Share Invite Link
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  color: COLORS.textMuted,
                  padding: "4px",
                  borderRadius: "4px",
                  transition: ANIMATIONS.hover
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#f1f5f9";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "none";
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: "20px" }}>
              <label style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "8px",
                color: COLORS.text
              }}>
                Player Link
              </label>
              <div style={{
                display: "flex",
                gap: "8px"
              }}>
                <input
                  value={inviteLink}
                  readOnly
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    background: "#f8fafc"
                  }}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(inviteLink);
                    // Could add a toast notification here
                  }}
                  style={{
                    padding: "12px 16px",
                    background: COLORS.primary,
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: ANIMATIONS.hover
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = COLORS.primaryHover;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = COLORS.primary;
                  }}
                >
                  Copy
                </button>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "8px",
                color: COLORS.text
              }}>
                Spectator Link
              </label>
              <div style={{
                display: "flex",
                gap: "8px"
              }}>
                <input
                  value={inviteLink + "&spectate=1"}
                  readOnly
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    background: "#f8fafc"
                  }}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(inviteLink + "&spectate=1");
                    // Could add a toast notification here
                  }}
                  style={{
                    padding: "12px 16px",
                    background: COLORS.secondary,
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: ANIMATIONS.hover
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#475569";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = COLORS.secondary;
                  }}
                >
                  Copy
                </button>
              </div>
            </div>

            <div style={{
              padding: "16px",
              background: "#f0fdf4",
              borderRadius: "8px",
              border: `1px solid ${COLORS.success}20`
            }}>
              <div style={{
                fontSize: "0.875rem",
                color: COLORS.text,
                lineHeight: "1.5"
              }}>
                <strong>Table Code:</strong> <code style={{
                  background: "#eff6ff",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontFamily: "monospace",
                  color: COLORS.primary
                }}>{tableCode}</code>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
